// ── Logbook evidence types ──────────────────────────────────────────────────
// Mirrors the Loglibrarian v1 contract (event schema 2) as proxied by
// `spy.playcdu.co/api/notes/logbook`. Field names match the wire format exactly so a record on
// screen and a record in the store are the same record.
//
// API v1 may add optional response fields, so every consumer here ignores unknown keys rather than
// failing on them, and explicit partial/unavailable states are preserved rather than smoothed over.

export type LogbookActorKind = 'player' | 'fake_player' | 'automation' | 'unknown';

export type LogbookOutcome = 'attempted' | 'cancelled' | 'completed' | 'denied' | 'observed' | 'unknown';

export const LOGBOOK_OUTCOMES: LogbookOutcome[] = ['completed', 'attempted', 'denied', 'cancelled', 'observed', 'unknown'];

/** One immutable evidence record. Every field is required by schema 2. */
export interface LogbookEvent {
  schema: 2;
  event_id: string;
  server_id: string;
  world_id: string;
  producer_id: string;
  sequence: number;
  /** UTC epoch milliseconds. 0 labels unknown legacy time. */
  timestamp: number;
  actor_id: string;
  actor_name: string;
  actor_kind: LogbookActorKind;
  action: string;
  outcome: LogbookOutcome;
  dimension: string;
  x: number;
  y: number;
  z: number;
  object_id: string;
  quantity: number;
  integration: string;
  /** At most 16 bounded strings: before/after states, attribution qualifiers, mod context. */
  metadata: Record<string, string>;
}

/** AND-combined filters. Omitted servers means every server the Argus credential may read. */
export interface LogbookQuery {
  servers?: string[];
  cluster?: string;
  world_id?: string;
  actor_id?: string;
  actor_name?: string;
  /** One exact action, or up to 32 exact actions separated by commas. */
  action?: string;
  outcome?: LogbookOutcome;
  dimension?: string;
  object_id?: string;
  namespace?: string;
  /** Inclusive UTC epoch milliseconds. */
  after?: number;
  /** Exclusive UTC epoch milliseconds. */
  before?: number;
  /** Inclusive [minX, minY, minZ, maxX, maxY, maxZ]. */
  box?: [number, number, number, number, number, number];
  limit?: number;
  cursor?: string;
}

export interface LogbookSearchResult {
  events: LogbookEvent[];
  /** Null once the snapshot is exhausted. Expires fifteen minutes after the first page. */
  cursor: string | null;
  source: 'central';
  /** The servers this answer actually covered. */
  scope: string[];
  /** Ingestion high water the page is pinned to; later insertions are excluded. */
  snapshot: number;
  state: 'ready';
  /** Always notes that producer backlog may exist. Never hide this from staff. */
  completeness: string;
  has_more: boolean;
}

export type LogbookGroupBy = 'action' | 'actor_id' | 'server_id' | 'namespace' | 'object_id' | 'outcome' | 'dimension' | 'time';

export type LogbookBucketMs = 60000 | 3600000 | 86400000;

export interface LogbookAggregationGroup {
  value: string | number;
  /** Counts every matching raw event in one read transaction, not one page. */
  event_count: number;
  /**
   * Earliest and latest event timestamps inside the group. Present from Loglibrarian
   * 2.0.0-alpha.7; an older service omits them, and the UI falls back to the loaded sample.
   */
  first_timestamp?: number;
  last_timestamp?: number;
}

export interface LogbookAggregationResult {
  kind: 'raw_event_aggregate';
  group_by: LogbookGroupBy;
  bucket_ms: number | null;
  groups: LogbookAggregationGroup[];
  scope: string[];
  source: 'central';
  snapshot: number;
  /** `partial` means groups were omitted past the 200 group ceiling. */
  state: 'ready' | 'partial';
  has_more_groups: boolean;
  completeness: string;
  window: {
    after_inclusive: number | null;
    before_exclusive: number | null;
    timezone: 'UTC';
  };
}

export interface LogbookAggregationQuery extends Omit<LogbookQuery, 'limit' | 'cursor'> {
  group_by?: LogbookGroupBy;
  bucket_ms?: LogbookBucketMs;
}

/** Server metadata is operator-defined: `server_id` is the only guaranteed key. */
export interface LogbookServer {
  server_id: string;
  cluster?: string;
  status?: string;
  [key: string]: string | undefined;
}

export interface LogbookServersResult {
  servers: LogbookServer[];
  [key: string]: unknown;
}

export interface LogbookSyncServer {
  server_id: string;
  /** Last tracked insertion receipt. Null means nothing has been received yet. */
  last_received_at: number | null;
}

export interface LogbookSyncResult {
  servers: LogbookSyncServer[];
  state: 'ready';
  producer_backlog: string;
  semantics: string;
}

export interface LogbookCapabilities {
  api: 1;
  event_schema: 2;
  version: string;
  limits: {
    batch: number;
    page: number;
    query_seconds: number;
    jobs: number;
    body_bytes: number;
  };
  features: string[];
  unavailable: string[];
}

export interface LogbookAdminStatus {
  api: 1;
  event_schema: 2;
  store_generation: string;
  durability: 'sqlite-wal-full';
  logical_used_bytes_including_wal: number;
  max_bytes: number;
  database_allocated_bytes: number;
  reusable_database_bytes: number;
  ingestion_row_high_water: number;
  high_water_semantics: string;
  retention_days: number;
  rollups: { enabled: boolean; telemetry_raw_days: number };
  jobs_retained: number;
  maintenance: {
    state: 'idle' | 'ready' | 'degraded';
    last_completed_at: number | null;
    rolled_up: number;
    pruned: number;
  };
}

export interface LogbookRollupGroup {
  server_id: string;
  world_id: string;
  producer_id: string;
  actor_id: string;
  dimension: string;
  window_start: number;
  samples: number;
  first_timestamp: number;
  last_timestamp: number;
  min_x: number;
  min_y: number;
  min_z: number;
  max_x: number;
  max_y: number;
  max_z: number;
}

export interface LogbookRollupResult {
  kind: 'telemetry_rollup';
  provenance: 'loglibrarian/player-state-hourly/v1';
  window_ms: 3600000;
  scope: string[];
  groups: LogbookRollupGroup[];
  has_more: boolean;
  completeness: string;
}

export interface LogbookRollupQuery {
  servers?: string[];
  cluster?: string;
  world_id?: string;
  actor_id?: string;
  dimension?: string;
  after?: number;
  before?: number;
}

export type LogbookJobKind = 'search' | 'export';

export interface LogbookJobSubmitted {
  job_id: string;
  state: 'pending';
}

export interface LogbookExportResult {
  /** One JSON object per line. */
  ndjson: string;
  cursor: string | null;
  [key: string]: unknown;
}

export interface LogbookJobResult {
  state: 'pending' | 'ready' | 'cancelled';
  result?: LogbookSearchResult | LogbookExportResult;
}

// ── Cases (Argus owned) ─────────────────────────────────────────────────────
// Cases touch Loglibrarian on no path at all. A record is sent whole from the page displaying it and
// stored in Argus's own database, so a case can be built and read while the evidence service is
// down, and it outlives central retention, a rollup and a scope change.

export type LogbookCaseStatus = 'open' | 'closed' | 'archived';

export type LogbookCaseItemType = 'note' | 'warning' | 'ban' | 'incident' | 'ticket' | 'appeal';

export type LogbookCaseSubjectRole = 'suspect' | 'affected' | 'witness';

export interface LogbookCase {
  id: string;
  /** Human-facing, unique, quotable in a ban reason: CASE-2026-0915-7F3A. */
  reference: string;
  title: string;
  summary: string | null;
  status: LogbookCaseStatus;
  created_by: string;
  created_at: number;
  updated_at: number;
  closed_by: string | null;
  closed_at: number | null;
  record_count: number;
  link_count: number;
  subject_count: number;
  /** Oldest and newest evidence timestamps held, ignoring records with no known time. */
  earliest_event_at: number | null;
  latest_event_at: number | null;
}

/** One permanent copy of a Logbook record, as stored. `event` is the whole schema 2 event. */
export interface LogbookCaseRecord {
  id: string;
  case_id: string;
  event_id: string;
  event: LogbookEvent | null;
  server_id: string | null;
  world_id: string | null;
  producer_id: string | null;
  sequence: number | null;
  event_timestamp: number | null;
  actor_id: string | null;
  actor_name: string | null;
  actor_kind: LogbookActorKind | null;
  action: string | null;
  outcome: LogbookOutcome | null;
  dimension: string | null;
  x: number | null;
  y: number | null;
  z: number | null;
  object_id: string | null;
  quantity: number | null;
  integration: string | null;
  note: string | null;
  added_by: string;
  added_at: number;
}

export interface LogbookCaseLink {
  id: string;
  case_id: string;
  item_type: LogbookCaseItemType;
  item_id: string;
  linked_by: string;
  linked_at: number;
}

export interface LogbookCaseSubject {
  id: string;
  case_id: string;
  subject_uuid: string;
  role: LogbookCaseSubjectRole;
  note: string | null;
  added_by: string;
  added_at: number;
}

export interface LogbookCaseDetail extends LogbookCase {
  records: LogbookCaseRecord[];
  links: LogbookCaseLink[];
  subjects: LogbookCaseSubject[];
}

export interface LogbookCasesResult {
  cases: LogbookCase[];
  total: number;
  limit: number;
  offset: number;
}

/** What an attach copied. There is no upstream call, so there is no partial failure to report. */
export interface LogbookAttachResult {
  attached: number;
  duplicates: number;
}

export interface LogbookCaseFilters {
  status?: LogbookCaseStatus;
  q?: string;
  /** A player named on the case. */
  subject?: string;
  /** A player who is the actor on the case's evidence. */
  actor?: string;
  event_id?: string;
  item_type?: LogbookCaseItemType;
  item_id?: string;
  limit?: number;
  offset?: number;
}

export interface CreateLogbookCaseRequest {
  title: string;
  summary?: string;
  reference?: string;
  /** Whole records, sent from the page displaying them. Argus never re-reads them from Logbook. */
  records?: LogbookEvent[];
  subjects?: { uuid: string; role?: LogbookCaseSubjectRole }[];
}
